import React, { FC, useCallback, useEffect, useState } from 'react';
import Button from '@/components/DesignLibrary/Button';
import Spacer from '@/components/Spacer';
import formatAmountWithCurrency from '@format/priceWithCurrency';
import { Currency, CurrencyUnknown, CurrencyValue } from '@/typings/api-models';
import { ApiDefinition } from '@documentsApi';
import { calculateMeansTest } from '@/tsClient/ai';
import { useToast } from '@/hooks';
import { ToastTypeVariants } from '@/context/ToastContext';
import AILabel from '@/components/AILabel';
import Modal from '@/components/Modal';
import { MeansTestAiResponse, MeansTestResponse } from '@/tsClient/servers/noodleDocuments/document-api-models';
import DOMPurify from 'isomorphic-dompurify';
import NoodleAI from '@/components/Icons/NoodleAI';
import Shimmer from '@/components/Shimmer';
import s from './DocumentRequestUserDetail.module.scss';

type Props = {
  meansTestData: MeansTestResponse | null;
  tableData: ApiDefinition['/document-request-users/{documentRequestUserId}/consolidated-data']['GET']['response'] | null;
  selectedForMeanTest: string[];
  userWorkflowId: string | null;
  documentRequestUserId: string;
  fetchingMeansTestData: boolean
};

type RequestPayload = {
  'Amount per Pay Period': number | null | CurrencyUnknown;
  'Overtime per Pay Period': number | null;
  'Pay Date': string | null;
  Employer: string | null;
  type: string | null;
};

const MeansTestSection: FC<Props> = ({
  meansTestData,
  tableData,
  selectedForMeanTest,
  userWorkflowId,
  documentRequestUserId,
  fetchingMeansTestData,
}) => {
  const [meansTestResponse, setMeansTestResponse] = useState<MeansTestResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [isReasoningModalOpen, setIsReasoningModalOpen] = useState(false);
  const toast = useToast();

  const meansTestDataKeys = [
    {
      key: 'grossMonthlyIncome',
      label: "2. Gross Monthly Income",
    },
    {
      key: 'alimonyAndMaintenancePayments',
      label: "3. Alimony And Maintenance Payments",
    },
    {
      key: 'rentalPropertyIncome',
      label: "6. Net income from rental and other real property",
    },
    {
      key: 'interestDividendsAndRoyalties',
      label: "7. Interest, Dividends And Royalties",
    },
    {
      key: 'unemploymentCompensation',
      label: "8. Unemployment compensation",
    },
    {
      key: 'pensionOrRetirementIncome',
      label: "9. Pension or retirement income",
    },
    {
      key: 'otherHouseholdIncome',
      label: "10. Income from all other sources not listed above",
    },
  ];

  const ListItem = useCallback(
    ({ label, value, key }) => (
      <div className={s.grossIncomeSection} key={key}>
        <span className="body-sm-bold">{label}:</span>
        <span className="body-sm">{value}</span>
      </div>
    ),
    [],
  );

  const normalizeValue = useCallback(
    (value: string | number | null | undefined): string => {
      if(value === null || value === undefined) return "--";
      if(typeof value === 'number') return formatAmountWithCurrency(value, Currency.Usd);
      if(typeof value === 'string') return Number.isNaN(Number(value)) ? "--" : formatAmountWithCurrency(Number(value), Currency.Usd);
      return "--";
    },
    [],
  );

  const handleMeansTestCalculation = async (): Promise<void> => {
    if (!tableData?.files?.length || !selectedForMeanTest?.length) return;
    if (!userWorkflowId) {
      toast(ToastTypeVariants.ERROR, 'User workflow id is missing');
      return;
    }
    const payload: RequestPayload[] = [];
    tableData.files.forEach(file => {
      if (file.documents?.length) {
        file.documents?.forEach(doc => {
          if (selectedForMeanTest.includes(doc.id)) {
            payload.push({
              'Amount per Pay Period': (doc.data?.tableData as Record<string, number>).amountPerPayPeriod,
              Employer: file.fileName,
              'Overtime per Pay Period': (doc.data?.tableData as Record<string, number>).overtimePerPayPeriod,
              'Pay Date': (doc.data?.tableData as Record<string, string>).payDate,
              type: 'Employment',
            });
          }
        });
      } else if (
        file.metadata
        && file.metadata.incomeSource
        && file.metadata.incomeSource !== 'Employment'
        && file.metadata.monthlyDollarValue.length
      ) {
        file.metadata.monthlyDollarValue.forEach((value: { month: string; income: CurrencyValue; }) => {
          if( selectedForMeanTest.includes(`${file.id}-${value.month}`)) {
            payload.push({
              'Amount per Pay Period': value.income.amount,
              Employer: null,
              'Overtime per Pay Period': 0,
              'Pay Date': value.month,
              type: file.fileName,
            });
          }
        });

      }
    });

    try {
      setLoading(true);
      setMeansTestResponse(null);
      const response = await calculateMeansTest({ data: payload, documentRequestUserId, userWorkflowId });
      setMeansTestResponse(response);
    } catch (error) {
      toast(ToastTypeVariants.ERROR, 'Ai failed to calculate means test');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (meansTestData) {
      setMeansTestResponse(meansTestData);
    }
  }, [meansTestData]);

  return (
    <div className={s.meansTestSectionContainer}>
      {isReasoningModalOpen && (
        <Modal title={<span className={s['title--ai']}>Means Test Calculator AI Reasoning</span>} onClose={() => setIsReasoningModalOpen(false)} drag>
          <div className={s.reasoningModalContent}>
            {loading || fetchingMeansTestData || meansTestResponse?.status === 'pending'
              ? (
                <>
                  <Spacer />
                  <AILabel label="Glade ai is calculating..." />
                </>
              )
              : (
                meansTestResponse?.data?.calculationExplanationHtml && (
                  <div
                    className={s.explanationHtml}
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(meansTestResponse?.data?.calculationExplanationHtml),
                    }}
                  />
                )
              )}
            <Spacer />
            {(!loading && !fetchingMeansTestData && meansTestResponse?.status !== 'pending') && <Button variant="ai" size="sm" fullWidth disabled={loading} onClick={handleMeansTestCalculation}>
              Recalculate
            </Button>}
          </div>
        </Modal>
      )}
      <div className="caption-bold">Means Test Calculator</div>
      {fetchingMeansTestData && <><Spacer/><Shimmer /></>}
      {(loading || meansTestResponse?.status === 'pending') && !fetchingMeansTestData
        ? (
          <>
            <Spacer />
            <div className={s.aiTopBanner}>
              <NoodleAI size={32} />
              <div className={s.textContainer}>
                <div>Glade Ai is calculating...</div>
                <div className={s.secondaryText}>You can leave the page.</div>
              </div>
            </div>
          </>
        )
        : (
          <>
            {!!meansTestResponse && !!Object.keys(meansTestResponse?.data).length && (
              <>
                <Spacer />
                {
                  meansTestDataKeys.map(({key, label}) => <ListItem
                    label={label}
                    value={normalizeValue(meansTestResponse.data[key as keyof MeansTestAiResponse])}
                    key={key}
                  />)
                }
              </>
            )}
            <Spacer />
            {!fetchingMeansTestData && <div className={s.ctaSection}>
              {!!meansTestResponse && !!Object.keys(meansTestResponse?.data).length && (
                <Button variant="secondary" size="sm" onClick={() => setIsReasoningModalOpen(true)}>
                  View Reasoning
                </Button>
              )}
              <Button variant="ai" size="sm" onClick={handleMeansTestCalculation}>
                {( meansTestResponse && Object.keys(meansTestResponse.data).length ) ? 'Recalculate' : 'Calculate'}
              </Button>
            </div>}

          </>
        )}
    </div>
  );
};

export default MeansTestSection;
